import { useEffect, useRef, useState } from "react";
import {
  api,
  fmtTime,
  fmtTL,
  SITE_HOST,
  type CreditPackage,
  type CreditSummary,
  type SmsLoginInit,
  type Webapp,
  type WebappSecrets,
} from "../api";
import { Logo } from "../components/Logo";
import { SmsLink } from "../components/SmsLink";
import { SmsQrCode, isMobile } from "../components/SmsQrCode";
import { Footer } from "../components/Footer";

export function Dashboard({ meEmail, onLogout }: { meEmail: string; onLogout: () => void }) {
  const [items, setItems] = useState<Webapp[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Webapp | null>(null);
  const [secrets, setSecrets] = useState<{ title: string; values: Partial<WebappSecrets> } | null>(
    null,
  );

  async function refresh() {
    setErr(null);
    try {
      const r = await api.listWebapps();
      setItems(r.items);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "error");
    }
  }

  async function deleteWebapp(w: Webapp) {
    if (!confirm(`“${w.name}” uygulaması pasifleştirilsin mi? Daha sonra aynı kod (${w.code}) ile yeniden açabilirsiniz.`)) {
      return;
    }
    try {
      await api.deleteWebapp(w.id);
      refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "error");
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  async function logout() {
    await api.logout().catch(() => undefined);
    onLogout();
  }

  return (
    <div className="app">
      <div className="topbar">
        <a className="brand" href={`https://${SITE_HOST}`}>
          <Logo size={28} title={SITE_HOST} />
          <h1>
            Portal <span className="brand-sep muted">/ {SITE_HOST}</span>
          </h1>
        </a>
        <div className="row" style={{ gap: 12 }}>
          <span className="muted">{meEmail}</span>
          <button className="secondary" onClick={logout}>
            Çıkış
          </button>
        </div>
      </div>

      <div className="content">
        <CreditsPanel />

        <div className="panel">
          <div className="row" style={{ justifyContent: "space-between" }}>
            <h2 style={{ flex: 1 }}>Uygulamalarım</h2>
            <div style={{ flex: "0 0 auto" }}>
              <button onClick={() => setCreating(true)}>Yeni uygulama</button>
            </div>
          </div>

          {err && <div className="error">{err}</div>}
          {items === null && <div className="muted">Yükleniyor…</div>}
          {items && items.length === 0 && (
            <div className="muted" style={{ padding: "12px 0" }}>
              Henüz uygulamanız yok. “Yeni uygulama” ile başlayın — API anahtarınızı ve
              callback URL’nizi alırsınız.
            </div>
          )}
          {items && items.length > 0 && (
            <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>İsim</th>
                  <th>Kod</th>
                  <th>Callback</th>
                  <th>Bu uygulama</th>
                  <th>Durum</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((w) => (
                  <tr key={w.id}>
                    <td data-label="İsim">{w.name}</td>
                    <td data-label="Kod">
                      {w.code_hidden ? (
                        <span className="muted">kaldırıldı</span>
                      ) : (
                        <code>{w.code}</code>
                      )}
                    </td>
                    <td data-label="Callback">
                      <code>{w.callback_url}</code>
                    </td>
                    <td data-label="Bu uygulama">
                      <code>{w.verified_count}</code>
                      <br />
                      <span className="muted" style={{ fontSize: 12 }}>
                        doğrulama
                      </span>
                    </td>
                    <td data-label="Durum">
                      <span className={`pill ${w.active ? "ok" : "bad"}`}>
                        {w.active ? "aktif" : "kapalı"}
                      </span>
                    </td>
                    <td className="actions" data-label="İşlemler">
                      <div className="action-group">
                        <button className="secondary" onClick={() => setEditing(w)}>
                          Düzenle
                        </button>
                        <button
                          className="secondary"
                          onClick={async () => {
                            if (!confirm("API anahtarını yenile? Eski anahtar geçersiz olacak.")) return;
                            try {
                              const r = await api.rotateApiKey(w.id);
                              setSecrets({
                                title: "Yeni API anahtarı",
                                values: { api_key: r.api_key },
                              });
                            } catch (e) {
                              setErr(e instanceof Error ? e.message : "error");
                            }
                          }}
                        >
                          Anahtar yenile
                        </button>
                        <button className="danger" onClick={() => deleteWebapp(w)}>
                          Sil
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>

        <IntegrationGuide />

        <PhonePanel />

        <AccountDataPanel onDeleted={onLogout} />
      </div>

      {creating && (
        <CreateModal
          onClose={() => setCreating(false)}
          onCreated={(s) => {
            setCreating(false);
            setSecrets({
              title: "Uygulama oluşturuldu",
              values: { api_key: s.api_key, hmac_secret: s.hmac_secret, webhook_url: s.webhook_url },
            });
            refresh();
          }}
          onReactivated={() => {
            setCreating(false);
            refresh();
          }}
        />
      )}
      {editing && (
        <EditModal
          webapp={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}
      {secrets && <SecretsModal title={secrets.title} values={secrets.values} onClose={() => setSecrets(null)} />}
      <Footer />
    </div>
  );
}

function paymentResultFromUrl(): "success" | "failed" | null {
  if (typeof window === "undefined") return null;
  const v = new URLSearchParams(window.location.search).get("payment");
  return v === "success" || v === "failed" ? v : null;
}

function CreditsPanel() {
  const [summary, setSummary] = useState<CreditSummary | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [buying, setBuying] = useState(false);
  const [notice, setNotice] = useState<"success" | "failed" | null>(null);

  async function refresh() {
    setErr(null);
    try {
      setSummary(await api.getCredits());
    } catch (e) {
      setErr(e instanceof Error ? e.message : "error");
    }
  }

  useEffect(() => {
    refresh();
    // iyzico redirects back to /?payment=success|failed — surface it, then
    // scrub the query so a refresh doesn't re-show the banner.
    const result = paymentResultFromUrl();
    if (result) {
      setNotice(result);
      window.history.replaceState(null, "", "/");
    }
  }, []);

  return (
    <div className="panel">
      <div className="row" style={{ justifyContent: "space-between" }}>
        <h2 style={{ flex: 1 }}>Kredi bakiyem</h2>
        <div style={{ flex: "0 0 auto" }}>
          <button onClick={() => setBuying(true)}>Kredi al</button>
        </div>
      </div>

      {notice === "success" && (
        <div className="pill ok" style={{ marginTop: 8 }}>
          Ödeme alındı, krediler hesabınıza eklendi.
        </div>
      )}
      {notice === "failed" && (
        <div className="pill bad" style={{ marginTop: 8 }}>
          Ödeme tamamlanmadı. Tekrar deneyebilirsiniz.
        </div>
      )}

      {err && <div className="error">{err}</div>}
      {summary === null && !err && <div className="muted">Yükleniyor…</div>}

      {summary && (
        <>
          <div className="row" style={{ gap: 24, marginTop: 12, alignItems: "flex-end" }}>
            <div style={{ flex: "0 0 auto" }}>
              <div style={{ fontSize: 40, fontWeight: 700, lineHeight: 1 }}>
                {summary.remaining.toLocaleString("tr-TR")}
              </div>
              <div className="muted">kalan kredi</div>
            </div>
            <div className="muted" style={{ fontSize: 13 }}>
              Deneme: <code>{summary.trial_credits.toLocaleString("tr-TR")}</code> · Satın alınan:{" "}
              <code>{summary.paid_credits.toLocaleString("tr-TR")}</code> · Kullanılan:{" "}
              <code>{summary.consumed_credits.toLocaleString("tr-TR")}</code>
            </div>
          </div>
          <p className="muted" style={{ marginTop: 12 }}>
            Krediler tüm uygulamalarınız arasında ortaktır; her başarılı doğrulama 1 kredi
            harcar. İlk 250 doğrulama ücretsizdir.
          </p>
        </>
      )}

      {buying && summary && (
        <BuyCreditsModal
          summary={summary}
          onClose={() => setBuying(false)}
          onPick={async (pkg) => {
            setErr(null);
            try {
              const r = await api.startCheckout(pkg.id);
              window.location.href = r.payment_page_url;
            } catch (e) {
              const code = e instanceof Error ? e.message : "error";
              setErr(
                code === "payments_unavailable"
                  ? "Ödeme şu anda kullanılamıyor."
                  : code === "payment_init_failed"
                    ? "Ödeme başlatılamadı, tekrar deneyin."
                    : "Bir hata oluştu, tekrar deneyin.",
              );
              setBuying(false);
            }
          }}
        />
      )}
    </div>
  );
}

function BuyCreditsModal({
  summary,
  onClose,
  onPick,
}: {
  summary: CreditSummary;
  onClose: () => void;
  onPick: (pkg: CreditPackage) => void;
}) {
  const [pending, setPending] = useState<string | null>(null);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>Kredi al</h3>
        <div className="muted" style={{ marginBottom: 8, fontSize: 13 }}>
          Fiyatlara KDV dahildir. Kredi başı{" "}
          {fmtTL(summary.pricing.price_per_credit_kurus)} (KDV hariç). Her başarılı doğrulama
          1 kredidir.
        </div>
        {!summary.iyzico_enabled && (
          <div className="pill bad" style={{ marginBottom: 12 }}>
            Ödeme altyapısı henüz etkin değil; satın alma geçici olarak kapalı.
          </div>
        )}
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Kredi</th>
                <th>Tutar (KDV dahil)</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {summary.packages.map((p) => (
                <tr key={p.id}>
                  <td>
                    <code>{p.credits.toLocaleString("tr-TR")}</code>
                  </td>
                  <td>
                    {fmtTL(p.gross_kurus)}
                    <br />
                    <span className="muted" style={{ fontSize: 12 }}>
                      {fmtTL(p.net_kurus)} + KDV {fmtTL(p.kdv_kurus)}
                    </span>
                  </td>
                  <td className="actions">
                    <button
                      disabled={!summary.iyzico_enabled || pending !== null}
                      onClick={() => {
                        setPending(p.id);
                        onPick(p);
                      }}
                    >
                      {pending === p.id ? "Yönlendiriliyor…" : "Satın al"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="row" style={{ marginTop: 16, justifyContent: "flex-end" }}>
          <div style={{ flex: "0 0 auto" }}>
            <button type="button" className="secondary" onClick={onClose}>
              Kapat
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function IntegrationGuide() {
  const [open, setOpen] = useState(false);
  return (
    <div className="panel">
      <div className="row" style={{ justifyContent: "space-between" }}>
        <h2 style={{ flex: 1 }}>Nasıl entegre edilir</h2>
        <div style={{ flex: "0 0 auto" }}>
          <button className="secondary" onClick={() => setOpen((v) => !v)}>
            {open ? "Gizle" : "Adımları göster"}
          </button>
        </div>
      </div>

      {open && (
        <ol className="guide">
          <li>
            <strong>Uygulama oluşturun.</strong> Yukarıdan “Yeni uygulama” ile bir
            kayıt açın; size <code>API anahtarı</code> ve <code>HMAC secret</code>{" "}
            verilir. Bu değerler yalnızca bir kez gösterilir — “Anahtarları indir”
            ile saklayın.
          </li>
          <li>
            <strong>OTP isteyin.</strong> Kullanıcı giriş yapmak istediğinde
            backend’iniz uç noktamızı çağırır:
            <pre>{`POST https://${SITE_HOST}/auth/init
X-API-Key: <api_key>
Content-Type: application/json

{ "phone": "+905551112233" }`}</pre>
            Yanıt olarak <code>code</code> ve <code>otp</code> döner.
          </li>
          <li>
            <strong>Kullanıcıya gösterin.</strong> Kullanıcıdan, kayıtlı
            telefonundan kısa numaramıza <code>&lt;code&gt; &lt;otp&gt;</code>{" "}
            (örn. <code>ABCD 482910</code>) mesajını göndermesini isteyin.
          </li>
          <li>
            <strong>Sonucu bekleyin.</strong> SMS 60 saniye içinde ulaşırsa
            <code> callback_url</code> adresinize imzalı bir bildirim göndeririz:
            <pre>{`POST <callback_url>
X-Login-Signature: t=<unix>,v1=<hex hmac sha256>

{ "type": "verified", "sessionId": "...", "phone": "+90..." }`}</pre>
            Yalnızca <code>verified</code> göndeririz; eşleşme olmazsa hiçbir
            bildirim gelmez — süre aşımını <code>expiresAt</code>’ten kendiniz
            anlarsınız.
          </li>
          <li>
            <strong>İmzayı doğrulayın.</strong> <code>HMAC secret</code> ile
            <code> {"${t}.${rawBody}"} </code> üzerinden SHA-256 HMAC hesaplayıp
            gelen <code>v1</code> ile karşılaştırın; eşleşiyorsa kullanıcıyı
            içeri alın. Kullanıcıyı <strong>callback’teki <code>phone</code></strong>{" "}
            ile giriş yapın — tarayıcının/istemcinin bildirdiği numaraya değil.
            <code>sessionId</code> ile <code>phone</code> birlikte imzalandığı
            için, kimse kendi numarasını doğrulatıp başkasınınkini iddia edemez.
          </li>
          <li>
            <strong>Frontend: QR kodu + SMS butonu gösterin.</strong> En iyi
            kullanıcı deneyimi için <em>mobilde</em>{" "}
            <code>{"<a href=\"sms:+90…?body=KOD OTP\">"}</code> ile SMS
            butonunu, <em>masaüstünde</em> QR kodu gösterin. Kullanıcı QR'ı
            okutunca veya butona basınca SMS uygulaması numara ve mesaj
            doldurulmuş halde açılır.
            <br /><br />
            <strong>Android QR uyumluluğu:</strong> Android kamera uygulamaları{" "}
            <code>sms:</code> protokolünü doğrudan tanımaz. QR kodun içine{" "}
            <code>sms:</code> URI'si yerine <strong>kendi sitenizde bir HTTPS
            yönlendirme sayfası</strong> koyun:
            <pre>{`// QR içeriği (HTTPS, sms: değil!):
https://yourapp.example/sms-redirect
  ?to=+90XXXXXXXXXX&body=WXKZ%20482910

// Yönlendirme sayfasının JS'i:
var p = new URLSearchParams(location.search);
var smsUri = 'sms:' + encodeURIComponent(p.get('to'))
  + '?body=' + encodeURIComponent(p.get('body'));
window.location.href = smsUri;
// → Tarayıcı açılır → sms: URI → SMS uygulaması`}</pre>
            Bu sayede Android kamera uygulaması normal bir web linki olarak
            tanır, tarayıcı açılır ve tarayıcı da <code>sms:</code> şemasını
            cihazın SMS uygulamasına iletir.
          </li>
        </ol>
      )}

      {open && (
        <div className="callout" style={{ marginTop: 16 }}>
          <h3 style={{ marginTop: 0 }}>Kendi numaranızı kullanın — 4 harfli ön ek olmadan</h3>
          <p className="muted">
            Yukarıdaki akışta kullanıcılar <strong>ortak SMS numaramıza</strong>{" "}
            <code>&lt;kod&gt; &lt;otp&gt;</code> (örn. <code>ABCD 482910</code>) gönderir. 4 harfli
            kod, ortak numaraya gelen SMS’in hangi uygulamaya ait olduğunu ayırt etmemiz için
            gereklidir.
          </p>
          <p className="muted">
            <strong>Kendi SMS numaranız veya gateway’iniz varsa</strong> bu ön eke gerek kalmaz.
            Uygulamanızı oluşturduğunuzda size özel bir <strong>Webhook URL</strong> veririz
            (örn. <code>https://otp.mioe.com.tr/webhook/sms/webapp/…</code>). Gateway’inizi gelen
            SMS’leri bu adrese iletecek şekilde ayarlayın; URL’deki anahtar uygulamanızı tanır,
            böylece kullanıcılarınız <strong>yalnızca 6 haneli kodu</strong> (örn.{" "}
            <code>482910</code>) gönderir.
          </p>
          <p className="muted">
            Webhook URL’nizi “Uygulama oluşturuldu” ekranında görürsünüz; daha sonra anahtarınızı
            “Anahtar yenile” akışından yenileyebilirsiniz. Kendi numaranızı operatöre ait{" "}
            <em>ithal edilmiş bir hat</em> olarak bağlamak isterseniz bizimle iletişime geçin.
          </p>
        </div>
      )}
    </div>
  );
}

function PhonePanel() {
  const [phone, setPhone] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [input, setInput] = useState("");
  const [init, setInit] = useState<SmsLoginInit | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [status, setStatus] = useState<string>("");
  const [remaining, setRemaining] = useState(0);

  async function loadMe() {
    try {
      const me = await api.me();
      setPhone(me.phone);
    } catch {
      /* ignore */
    } finally {
      setLoaded(true);
    }
  }

  useEffect(() => {
    loadMe();
  }, []);

  useEffect(() => {
    if (!init) return;
    const target = Date.parse(init.expiresAt);
    const tick = () => setRemaining(Math.max(0, Math.round((target - Date.now()) / 1000)));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [init]);

  const pollRef = useRef<number | null>(null);
  useEffect(() => {
    if (!init) return;
    let cancelled = false;
    async function check() {
      try {
        const r = await api.phoneBindStatus(init!.sessionId);
        if (cancelled) return;
        if (r.status === "verified") {
          setStatus("Telefon doğrulandı ve hesabınıza bağlandı.");
          setPhone(r.phone);
          setInit(null);
          return;
        }
        if (r.status === "phone_taken") {
          setStatus("Bu numara başka bir hesaba bağlı.");
          setInit(null);
          return;
        }
        if (r.status === "timeout") {
          setStatus("SMS gelmeden kod süresi doldu. Tekrar deneyin.");
          setInit(null);
          return;
        }
        if (r.status === "failed") {
          setStatus("Bu kod artık geçerli değil. Tekrar deneyin.");
          setInit(null);
          return;
        }
        pollRef.current = window.setTimeout(check, 2000);
      } catch (e) {
        if (cancelled) return;
        setStatus(e instanceof Error ? e.message : "poll_failed");
      }
    }
    pollRef.current = window.setTimeout(check, 1500);
    return () => {
      cancelled = true;
      if (pollRef.current) window.clearTimeout(pollRef.current);
    };
  }, [init]);

  async function startBind(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    setStatus("");
    try {
      const r = await api.phoneBindInit(input.trim());
      setInit(r);
      setStatus("SMS'inizi bekliyoruz…");
    } catch (e) {
      const code = e instanceof Error ? e.message : "error";
      setErr(
        code === "phone_taken"
          ? "Bu numara başka bir hesaba bağlı."
          : code === "missing_phone"
            ? "Geçerli bir telefon numarası girin."
            : "İşlem başlatılamadı, tekrar deneyin.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function unbind() {
    if (!confirm("Telefonu kaldır? SMS ile giriş devre dışı kalır.")) return;
    try {
      await api.unbindPhone();
      setPhone(null);
      setInput("");
      setStatus("");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "error");
    }
  }

  return (
    <div className="panel">
      <h2>SMS ile giriş</h2>
      <p className="muted">
        Telefonunuzu bağlayın; sonra parola olmadan, firmamızın SMS numarasına tek kullanımlık
        kod göndererek giriş yapabilirsiniz.
      </p>

      {!loaded ? (
        <div className="muted">Yükleniyor…</div>
      ) : init ? (
        <div>
          <p className="muted">
            Bağlamak istediğiniz telefondan, firmamızın SMS numarasına aşağıdaki mesajı gönderin:
          </p>
          <div className="code-display">{`${init.code} ${init.otp}`}</div>
          {isMobile() ? (
            <SmsLink inboundNumber={init.inboundNumber} body={`${init.code} ${init.otp}`} />
          ) : (
            <SmsQrCode inboundNumber={init.inboundNumber} body={`${init.code} ${init.otp}`} />
          )}
          <p className="muted" style={{ marginTop: 12 }}>{remaining}s içinde geçerli.</p>
          {status && (
            <div className="muted" style={{ marginTop: 8 }}>
              {status}
            </div>
          )}
          <button
            type="button"
            className="secondary"
            style={{ marginTop: 12 }}
            onClick={() => {
              setInit(null);
              setStatus("");
            }}
          >
            Vazgeç
          </button>
        </div>
      ) : phone ? (
        <div>
          <div className="row" style={{ gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ flex: "1 1 auto", minWidth: 0 }}>
              Bağlı telefon: <code>{phone}</code>
            </span>
            <span className="pill ok" style={{ flex: "0 0 auto" }}>
              aktif
            </span>
          </div>
          {status && (
            <div className="muted" style={{ marginTop: 8 }}>
              {status}
            </div>
          )}
          <button type="button" className="secondary" style={{ marginTop: 12 }} onClick={unbind}>
            Telefonu kaldır
          </button>
        </div>
      ) : (
        <form onSubmit={startBind} style={{ maxWidth: 360 }}>
          <label>Telefon</label>
          <input
            type="tel"
            autoComplete="tel"
            placeholder="+90 5xx xxx xx xx"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            required
          />
          {err && <div className="error">{err}</div>}
          {status && (
            <div className="muted" style={{ marginTop: 8 }}>
              {status}
            </div>
          )}
          <div style={{ marginTop: 12 }}>
            <button type="submit" disabled={busy}>
              {busy ? "…" : "Telefon ekle"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function AccountDataPanel({ onDeleted }: { onDeleted: () => void }) {
  const [busy, setBusy] = useState<"export" | "delete" | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function exportData() {
    setBusy("export");
    setErr(null);
    try {
      const data = await api.exportMyData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "verilerim.json";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "error");
    } finally {
      setBusy(null);
    }
  }

  async function deleteAccount() {
    if (
      !confirm(
        "Hesabınız ve ona bağlı tüm uygulamalar, anahtarlar ve veriler kalıcı olarak silinecek. Bu işlem geri alınamaz. Devam edilsin mi?",
      )
    )
      return;
    setBusy("delete");
    setErr(null);
    try {
      await api.deleteMyAccount();
      onDeleted();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "error");
      setBusy(null);
    }
  }

  return (
    <div className="panel">
      <h2>Verilerim</h2>
      <p className="muted">
        KVKK kapsamındaki haklarınız: hakkınızda tuttuğumuz verileri indirebilir veya hesabınızı
        tümüyle silebilirsiniz.
      </p>
      {err && <div className="error">{err}</div>}
      <div className="row" style={{ gap: 8, marginTop: 12 }}>
        <button className="secondary" onClick={exportData} disabled={busy !== null}>
          {busy === "export" ? "Hazırlanıyor…" : "Verilerimi indir"}
        </button>
        <button className="danger" onClick={deleteAccount} disabled={busy !== null}>
          {busy === "delete" ? "Siliniyor…" : "Hesabımı sil"}
        </button>
      </div>
    </div>
  );
}

function createFriendly(code: string): string {
  switch (code) {
    case "invalid_callback_url":
      return "Geçerli bir callback URL girin (http/https).";
    case "missing_name":
      return "İsim gerekli.";
    case "invalid_code":
      return "Kod tam 4 harf olmalı (A–Z).";
    case "reserved_code":
      return "Bu kod ayrılmış, başka bir kod seçin.";
    case "code_taken":
      return "Bu kod başka bir uygulamada kullanılıyor, farklı bir kod seçin.";
    default:
      return "Oluşturulamadı, tekrar deneyin.";
  }
}

function CreateModal({
  onClose,
  onCreated,
  onReactivated,
}: {
  onClose: () => void;
  onCreated: (s: Webapp & WebappSecrets) => void;
  onReactivated: () => void;
}) {
  const [name, setName] = useState("");
  const [callback, setCallback] = useState("");
  const [code, setCode] = useState("");
  // "shared": users text "<code> <otp>" to the operator's shared number, so a
  // 4-letter code is required. "own": the customer runs their own SMS
  // number/gateway and routes inbound SMS to their webhook URL, so no code is
  // needed (the server auto-generates one).
  const [mode, setMode] = useState<"shared" | "own">("shared");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Set when the chosen code matches a soft-deleted app the customer owns; we
  // then offer to reactivate it instead of creating a new one.
  const [reactivatable, setReactivatable] = useState<Webapp | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const r = await api.createWebapp({
        name: name.trim(),
        callback_url: callback.trim(),
        // Own-number mode: send no code; the server auto-generates one.
        code: mode === "shared" ? code.trim().toUpperCase() : "",
      });
      if (r.kind === "created") onCreated(r.webapp);
      else setReactivatable(r.existing);
    } catch (e) {
      setErr(createFriendly(e instanceof Error ? e.message : "error"));
    } finally {
      setBusy(false);
    }
  }

  async function reactivate() {
    if (!reactivatable) return;
    setBusy(true);
    setErr(null);
    try {
      await api.reactivateWebapp(reactivatable.id);
      onReactivated();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "error");
    } finally {
      setBusy(false);
    }
  }

  if (reactivatable) {
    const remaining =
      reactivatable.quota_remaining === null ? "∞" : reactivatable.quota_remaining;
    return (
      <div className="modal-backdrop" onClick={onClose}>
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <h3>Bu kod zaten var</h3>
          <p className="muted">
            <code>{reactivatable.code}</code> koduyla daha önce oluşturup sildiğiniz bir uygulama
            var: <strong>{reactivatable.name}</strong>. Yeniden aktifleştirmek ister misiniz?
            Anahtarlarınız değişmeden geri gelir.
          </p>
          <p className="muted" style={{ marginTop: 8 }}>
            Kalan kullanım: <code>{remaining}</code>
            {reactivatable.quota_remaining !== null ? " doğrulama" : ""}.
          </p>
          {err && <div className="error">{err}</div>}
          <div className="row" style={{ marginTop: 16, justifyContent: "flex-end" }}>
            <div style={{ flex: "0 0 auto" }}>
              <button type="button" className="secondary" onClick={() => setReactivatable(null)}>
                Vazgeç
              </button>{" "}
              <button type="button" onClick={reactivate} disabled={busy}>
                {busy ? "…" : "Yeniden aktifleştir"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3>Yeni uygulama</h3>
        <label>İsim</label>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="My App" required />

        <label>SMS numarası</label>
        <div className="radio-cards">
          <label className={`radio-card ${mode === "shared" ? "selected" : ""}`}>
            <input
              type="radio"
              name="sms-mode"
              checked={mode === "shared"}
              onChange={() => setMode("shared")}
            />
            <div>
              <strong>Ortak SMS numaramız</strong>
              <div className="muted" style={{ fontSize: 12 }}>
                Kullanıcılarınız <code>&lt;kod&gt; &lt;otp&gt;</code> (örn. <code>ABCD 482910</code>)
                gönderir. 4 harfli kod gerekir.
              </div>
            </div>
          </label>
          <label className={`radio-card ${mode === "own" ? "selected" : ""}`}>
            <input
              type="radio"
              name="sms-mode"
              checked={mode === "own"}
              onChange={() => setMode("own")}
            />
            <div>
              <strong>Kendi numaram / SMS gateway'im</strong>
              <div className="muted" style={{ fontSize: 12 }}>
                Gelen SMS'leri size vereceğimiz <strong>Webhook URL</strong>'ye yönlendirirsiniz.
                Kod gerekmez — kullanıcılar yalnızca 6 haneli kodu (örn. <code>482910</code>)
                gönderir.
              </div>
            </div>
          </label>
        </div>

        {mode === "shared" ? (
          <>
            <label>Kod (4 harf)</label>
            <input
              value={code}
              onChange={(e) =>
                setCode(e.target.value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 4))
              }
              placeholder="ABCD"
              maxLength={4}
              style={{ textTransform: "uppercase", letterSpacing: "0.2em" }}
              required
            />
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              Kullanıcılarınızın SMS'in başına yazacağı 4 harfli kod; ortak numaraya gelen SMS'in
              hangi uygulamaya ait olduğunu ayırt etmemizi sağlar.
            </div>
          </>
        ) : (
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            Uygulamayı oluşturduğunuzda size özel bir <strong>Webhook URL</strong> vereceğiz.
            Kendi SMS gateway'inizi gelen mesajları bu adrese iletecek şekilde ayarlayın; kodu biz
            otomatik üretiriz, siz kullanmazsınız. Ayrıntı için “Nasıl entegre edilir”
            bölümüne bakın.
          </div>
        )}

        <label>Callback URL</label>
        <input
          value={callback}
          onChange={(e) => setCallback(e.target.value)}
          placeholder="https://yourapp.example/sms-login/cb"
          required
        />
        <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
          Doğrulama sonucu (verified/timeout) bu adrese imzalı olarak gönderilir.
        </div>
        {err && <div className="error">{err}</div>}
        <div className="row" style={{ marginTop: 16, justifyContent: "flex-end" }}>
          <div style={{ flex: "0 0 auto" }}>
            <button type="button" className="secondary" onClick={onClose}>
              Vazgeç
            </button>{" "}
            <button type="submit" disabled={busy}>
              {busy ? "…" : "Oluştur"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function EditModal({
  webapp,
  onClose,
  onSaved,
}: {
  webapp: Webapp;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(webapp.name);
  const [callback, setCallback] = useState(webapp.callback_url);
  const [active, setActive] = useState(webapp.active);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api.updateWebapp(webapp.id, {
        name: name.trim(),
        callback_url: callback.trim(),
        active,
      });
      onSaved();
    } catch (e) {
      const code = e instanceof Error ? e.message : "error";
      setErr(
        code === "invalid_callback_url" ? "Geçerli bir callback URL girin." : "Kaydedilemedi.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3>Uygulamayı düzenle</h3>
        <label>İsim</label>
        <input value={name} onChange={(e) => setName(e.target.value)} required />
        <label>Callback URL</label>
        <input value={callback} onChange={(e) => setCallback(e.target.value)} required />
        <label className="row" style={{ gap: 8, alignItems: "center" }}>
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
            style={{ width: "auto" }}
          />
          <span>Aktif</span>
        </label>
        <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
          Oluşturulma: {fmtTime(webapp.created_at)}
        </div>
        {err && <div className="error">{err}</div>}
        <div className="row" style={{ marginTop: 16, justifyContent: "flex-end" }}>
          <div style={{ flex: "0 0 auto" }}>
            <button type="button" className="secondary" onClick={onClose}>
              Vazgeç
            </button>{" "}
            <button type="submit" disabled={busy}>
              {busy ? "…" : "Kaydet"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function downloadSecrets(values: Partial<WebappSecrets>) {
  const lines = [
    `# ${SITE_HOST} — uygulama anahtarları`,
    "# Bu değerleri gizli tutun, kaynak koduna eklemeyin.",
    `# İndirilme: ${new Date().toISOString()}`,
    "",
  ];
  if (values.api_key) lines.push(`API_KEY=${values.api_key}`);
  if (values.hmac_secret) lines.push(`HMAC_SECRET=${values.hmac_secret}`);
  if (values.webhook_url) lines.push(`WEBHOOK_URL=${values.webhook_url}`);
  const blob = new Blob([lines.join("\n") + "\n"], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "mioe-login-keys.env";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function SecretsModal({
  title,
  values,
  onClose,
}: {
  title: string;
  values: Partial<WebappSecrets>;
  onClose: () => void;
}) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>{title}</h3>
        <div className="muted" style={{ marginBottom: 8 }}>
          Bu değerler yalnızca bir kez gösterilir. Güvenli bir yere kaydedin.
        </div>
        {values.api_key && (
          <>
            <label>API anahtarı (X-API-Key)</label>
            <pre>{values.api_key}</pre>
          </>
        )}
        {values.hmac_secret && (
          <>
            <label>HMAC secret (callback imzası)</label>
            <pre>{values.hmac_secret}</pre>
          </>
        )}
        {values.webhook_url && (
          <>
            <label>Webhook URL (kendi SMS gateway’iniz için)</label>
            <pre>{values.webhook_url}</pre>
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              Kendi SMS numaranız/gateway’iniz varsa, gelen SMS’leri bu adrese yönlendirin.
              Bu durumda kullanıcılarınız 4 harfli ön eke gerek kalmadan{" "}
              <strong>yalnızca 6 haneli kodu</strong> gönderir; uygulamanızı bu URL’deki anahtar
              tanır.
            </div>
          </>
        )}
        <div className="row" style={{ marginTop: 16, justifyContent: "flex-end" }}>
          <div style={{ flex: "0 0 auto" }}>
            <button type="button" className="secondary" onClick={() => downloadSecrets(values)}>
              Anahtarları indir
            </button>{" "}
            <button onClick={onClose}>Kapat</button>
          </div>
        </div>
      </div>
    </div>
  );
}
